import type { CustomerStatus, CustomerSummary } from '@central/api';
import { Card, PageHeader, StatusChip, formatDate } from '@central/ui';
import { Alert, Button, Group, Modal, Select, Table, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import { keys, useApi } from '../api';
import { DataTable, Empty, LoadMore, QueryState } from '../components/parts';
import { ResellerFilter } from '../components/ResellerFilter';
import { customerStatusChip, mono, muted } from '../lib/labels';
import { useCanWrite } from '../lib/me';
import { errorMessage } from '../lib/errors';

/**
 * Customers list: search by name, license key or environment name; filter by
 * status and by reseller (or the customers Rethink supports directly).
 */
export function Customers() {
  const api = useApi();
  const navigate = useNavigate({ from: '/customers' });
  const search = useSearch({ from: '/app/customers' });
  const canWrite = useCanWrite();
  const [text, setText] = useState(search.q ?? '');
  const [query] = useDebouncedValue(text.trim(), 250);
  const status = search.status ?? '';
  const resellerId = search.resellerId ?? '';

  useEffect(() => {
    if ((search.q ?? '') !== query)
      void navigate({ search: (s) => ({ ...s, q: query || undefined }), replace: true });
  }, [query, search.q, navigate]);

  const list = useInfiniteQuery({
    queryKey: keys.customerList(query, status, resellerId),
    queryFn: ({ pageParam }) =>
      api.customers.list({
        query: query || undefined,
        status: search.status,
        resellerId: search.resellerId,
        cursor: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <>
      <PageHeader
        title="Customers"
        actions={
          canWrite && (
            <Button
              color="dark"
              size="sm"
              h={40}
              onClick={() => void navigate({ search: (s) => ({ ...s, new: true }) })}
            >
              New customer
            </Button>
          )
        }
      />
      <Group gap={10} wrap="wrap">
        <TextInput
          aria-label="Search customers"
          placeholder="Name, license key or environment…"
          size="sm"
          w={320}
          maw="100%"
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
        />
        <Select
          aria-label="Status"
          size="sm"
          w={180}
          maw="100%"
          value={status}
          onChange={(v) =>
            void navigate({
              search: (s) => ({ ...s, status: (v || undefined) as CustomerStatus | undefined }),
              replace: true,
            })
          }
          data={[
            { value: '', label: 'Any status' },
            { value: 'active', label: 'Active' },
            { value: 'suspended', label: 'Suspended' },
            { value: 'archived', label: 'Archived' },
          ]}
          allowDeselect={false}
        />
        <ResellerFilter
          value={resellerId}
          onChange={(v) =>
            void navigate({
              search: (s) => ({ ...s, resellerId: v || undefined }),
              replace: true,
            })
          }
        />
      </Group>
      <Card padded={false}>
        <QueryState isPending={list.isPending} error={list.error}>
          {() =>
            items.length === 0 ? (
              <Empty>
                {query || status || resellerId ? 'No customers match.' : 'No customers yet.'}
              </Empty>
            ) : (
              <DataTable
                label="Customers"
                minWidth={960}
                head={[
                  'Customer',
                  'Status',
                  'Reseller',
                  'Environments',
                  'Licenses',
                  'Next license expiry',
                  'Maintenance ends',
                  'Account manager',
                ]}
              >
                {items.map((c) => (
                  <CustomerRow key={c.id} c={c} />
                ))}
              </DataTable>
            )
          }
        </QueryState>
        {list.hasNextPage && (
          <LoadMore loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()} />
        )}
      </Card>
      <NewCustomer
        opened={!!search.new && canWrite}
        onClose={() => void navigate({ search: (s) => ({ ...s, new: undefined }) })}
      />
    </>
  );
}

function CustomerRow({ c }: { c: CustomerSummary }) {
  const chip = customerStatusChip[c.status];
  const h = c.health;
  return (
    <Table.Tr>
      <Table.Td style={{ fontWeight: 600 }}>
        <Link to="/customers/$customerId" params={{ customerId: c.id }}>
          {c.name}
        </Link>
      </Table.Td>
      <Table.Td>
        <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
      </Table.Td>
      <Table.Td>
        <ResellerCell reseller={c.reseller} />
      </Table.Td>
      <Table.Td>
        <Group gap={6} wrap="nowrap" aria-label={`${c.environmentCount} environments`}>
          <span style={mono}>{c.environmentCount}</span>
          {h.ok > 0 && <StatusChip tone="ok">{h.ok}</StatusChip>}
          {h.warn > 0 && <StatusChip tone="warn">{h.warn}</StatusChip>}
          {h.down > 0 && <StatusChip tone="down">{h.down}</StatusChip>}
          {h.unk > 0 && <StatusChip tone="unk">{h.unk}</StatusChip>}
        </Group>
      </Table.Td>
      <Table.Td style={mono}>{c.licenseCount}</Table.Td>
      <Table.Td>{formatDate(c.nextLicenseExpiry)}</Table.Td>
      <Table.Td>
        {formatDate(c.maintenance.endsAt)}
        {c.maintenance.coversThrough && (
          <span style={{ ...muted, fontSize: 13 }}> · to {c.maintenance.coversThrough}</span>
        )}
      </Table.Td>
      <Table.Td style={muted}>{c.accountManager?.name ?? '–'}</Table.Td>
    </Table.Tr>
  );
}

/** The customer's reseller, linking to its page, or a muted "Direct". */
export function ResellerCell({ reseller }: { reseller: CustomerSummary['reseller'] }) {
  return reseller ? (
    <Link to="/resellers/$resellerId" params={{ resellerId: reseller.id }}>
      {reseller.name}
    </Link>
  ) : (
    <span style={muted}>Direct</span>
  );
}

function NewCustomer({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string>();
  const create = useMutation({
    mutationFn: () => api.customers.create({ name: name.trim() }),
    onSuccess: async (c) => {
      queryClient.setQueryData(keys.customer(c.id), c);
      await queryClient.invalidateQueries({ queryKey: keys.customers });
      await queryClient.invalidateQueries({ queryKey: keys.overview });
      setName('');
      await navigate({ to: '/customers/$customerId', params: { customerId: c.id } });
    },
    onError: (e) => {
      const f = (e as { fields?: Record<string, string> }).fields;
      if (f?.name) setNameError('Enter the customer’s name.');
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setNameError('Enter the customer’s name.');
    setNameError(undefined);
    create.mutate();
  };

  return (
    <Modal opened={opened} onClose={onClose} title="New customer">
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <TextInput
          label="Name"
          data-autofocus
          value={name}
          error={nameError}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        {create.error && !nameError && <Alert color="red">{errorMessage(create.error)}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" color="dark" loading={create.isPending}>
            Create customer
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
